// ─── 🔒 Il lucchetto dei no-show ripetuti (R16), nella scheda del socio ──────
//
// 🆕 26/09/2026, fase 2 della caparra. Dal documento: *«Lo staff vede il flag e
// può rimuoverlo con motivazione»*. Il lucchetto non si scrive: lo calcola il
// gestionale dalle partite saltate. «Togli» scrive chi l'ha tolto e perché.
// Senza lucchetto e senza partite saltate, il riquadro non c'è.
// ↩️ Voce 30: sotto la soglia dice quante partite ha saltato nella finestra, e
// mostra i no-show annullati, barrati, con chi, quando e perché.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { giornoPerEsteso, type BloccoNoShow } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Guasto } from '../../components/pezzi.js';

export function LucchettoNoShow({
  id,
  puòTogliere,
}: {
  id: string;
  puòTogliere: boolean;
}): React.JSX.Element | null {
  const qc = useQueryClient();
  const [apre, setApre] = useState(false);
  const [motivo, setMotivo] = useState('');
  const b = useQuery({
    queryKey: ['lucchetto', id],
    queryFn: () => chiama<BloccoNoShow>('GET', `/members/${id}/no-show-flag`),
  });
  const togli = useMutation({
    meta: { fatto: 'blocco da no-show tolto: il socio prenota come tutti' },
    mutationFn: () =>
      chiama<BloccoNoShow>('POST', `/members/${id}/no-show-flag/remove`, { reason: motivo.trim() }),
    onSuccess: (dopo) => {
      qc.setQueryData(['lucchetto', id], dopo);
      setApre(false);
      setMotivo('');
    },
  });
  if (!b.data) return null;
  const f = b.data;
  const annullati = (
    <>
      {f.cancelled.map((x) => (
        <p key={`${x.date}-${x.at}`} className="annullo-no-show">
          <s>{giornoPerEsteso(x.date)}</s> · no-show annullato il {new Date(x.at).toLocaleDateString('it-IT')}
          {x.by ? ` da ${x.by}` : ''}: «{x.reason}»
        </p>
      ))}
    </>
  );
  if (!f.active) {
    if (!f.removed && f.recent.length === 0 && f.cancelled.length === 0) return null;
    return (
      <section className="riquadro">
        <h3>No-show</h3>
        {(f.recent.length > 0 || f.cancelled.length > 0) && (
          <p className="piccolo">
            Nessun lucchetto.{' '}
            {f.recent.length === 0
              ? `Negli ultimi ${f.months} mesi non ha saltato partite.`
              : `Negli ultimi ${f.months} mesi: ${f.recent.length === 1 ? '1 partita saltata' : `${f.recent.length} partite saltate`} (${f.recent.map((x) => giornoPerEsteso(x)).join(', ')}).`}
          </p>
        )}
        {annullati}
        {f.removed && (
          <p className="piccolo tenue">
            Lucchetto tolto il {new Date(f.removed.at).toLocaleDateString('it-IT')}
            {f.removed.by ? ` da ${f.removed.by}` : ''}: {f.removed.reason}
          </p>
        )}
      </section>
    );
  }
  return (
    <section className="riquadro">
      <h3>No-show</h3>
      <p className="avvertenza">
        🔒 <strong>Acconto sempre, fino a {f.until ? giornoPerEsteso(f.until) : '—'}</strong>: ha saltato{' '}
        {f.dates.length} partite ({f.dates.map((x) => giornoPerEsteso(x)).join(', ')}).
      </p>
      {annullati}
      {puòTogliere && !apre && (
        <button type="button" className="bordato" onClick={() => setApre(true)}>
          Togli il lucchetto
        </button>
      )}
      {apre && (
        <div className="finestra-caparra">
          <label className="campo">
            <span>Perché lo togli (resta scritto)</span>
            <input id="motivo-lucchetto" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </label>
          <div className="azioni-caparra">
            <button
              type="button"
              disabled={motivo.trim().length < 3 || togli.isPending}
              onClick={() => togli.mutate()}
            >
              Togli il lucchetto
            </button>
            <button type="button" className="bordato" onClick={() => setApre(false)}>
              Lascia stare
            </button>
          </div>
          <Guasto errore={togli.error} />
        </div>
      )}
    </section>
  );
}
